'use client';
import React from 'react';
import MuiButton from '@mui/material/Button';
import MuiIconButton from '@mui/material/IconButton';
import MuiChip from '@mui/material/Chip';
import MuiDivider from '@mui/material/Divider';
import MuiSwitch from '@mui/material/Switch';
import MuiDialog from '@mui/material/Dialog';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import FormHelperText from '@mui/material/FormHelperText';
import MuiSkeleton from '@mui/material/Skeleton';
import MuiTooltip from '@mui/material/Tooltip';
import MuiCheckbox from '@mui/material/Checkbox';
import LinearProgress from '@mui/material/LinearProgress';
import MuiTabs from '@mui/material/Tabs';
import MuiTab from '@mui/material/Tab';

/** Set these to the same hex values as your tailwind.config colors. */
export const colors = {
  primary50: '#EFF8FF',
  primary100: '#D1E9FF',
  primary200: '#B2DDFF',
  primary600: '#1570EF',
  primary700: '#175CD3',
  error100: '#FEE4E2',
  error500: '#F04438',
  error600: '#D92D20',
  error700: '#B42318',
  success600: '#12B76A',
  purple50: '#F4F3FF',
  purple200: '#D9D6FE',
  purple700: '#5925DC',
  gray50: '#F9FAFB',
  gray100: '#F2F4F7',
  gray200: '#EAECF0',
  gray300: '#D0D5DD',
  gray400: '#98A2B3',
  gray500: '#667085',
  gray600: '#475467',
  gray700: '#344054',
};

const intentColors = {
  primary: {
    main: colors.primary600,
    hover: colors.primary700,
    soft: colors.primary100,
  },
  error: {
    main: colors.error600,
    hover: colors.error700,
    soft: colors.error100,
  },
  warning: { main: '#DC6803', hover: '#B54708', soft: '#FEF0C7' },
  default: { main: colors.gray700, hover: colors.gray50, soft: colors.gray100 },
};

/* ------------------------------- Button ---------------------------------- */

export const Button = ({
  variant = 'filled',
  intent = 'primary',
  size = 'sm',
  ...rest
}) => {
  const c = intentColors[intent];

  const variantSx =
    variant === 'filled'
      ? { bgcolor: c?.main, color: '#fff', '&:hover': { bgcolor: c?.hover } }
      : variant === 'outlined'
        ? {
            border: `1px solid ${intent === 'default' ? colors.gray300 : c?.main}`,
            color: c?.main,
            '&:hover': { bgcolor: colors.gray50 },
          }
        : {
            bgcolor: c.soft,
            color: c.main,
            '&:hover': { bgcolor: c.soft, filter: 'brightness(0.97)' },
          };

  return (
    <MuiButton
      disableElevation
      {...rest}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 14,
        lineHeight: '20px',
        borderRadius: '8px',
        minWidth: 0,
        px: '14px',
        py: size === 'sm' ? '8px' : '10px',
        ...variantSx,
        '&.Mui-disabled': {
          bgcolor: variant === 'filled' ? colors.gray200 : 'transparent',
          color: colors.gray400,
          borderColor: colors.gray200,
        },
      }}
    />
  );
};

/* ----------------------------- IconButton -------------------------------- */

export const IconButton = ({
  intent = 'default',
  variant = 'outlined',
  ...rest
}) => {
  const main = intent === 'error' ? colors.error600 : colors.gray700;
  const variantSx =
    variant === 'outlined'
      ? {
          border: `1px solid ${intent === 'error' ? colors.error600 : colors.gray600}`,
          '&:hover': { bgcolor: colors.gray50 },
        }
      : variant === 'soft'
        ? {
            bgcolor: intent === 'error' ? colors.error100 : colors.primary100,
            '&:hover': { filter: 'brightness(0.97)' },
          }
        : { '&:hover': { bgcolor: colors.gray100 } };
  return (
    <MuiIconButton
      {...rest}
      sx={{ p: '8px', borderRadius: '8px', color: main, ...variantSx }}
    />
  );
};

/* -------------------------------- Chip ----------------------------------- */

const chipStyles = {
  primary: {
    bg: colors.primary50,
    color: colors.primary700,
    border: colors.primary200,
  },
  purple: {
    bg: colors.purple50,
    color: colors.purple700,
    border: colors.purple200,
  },
  default: {
    bg: colors.gray100,
    color: colors.gray700,
    border: colors.gray200,
  },
  success: { bg: '#ECFDF3', color: '#027A48', border: '#ABEFC6' },
  warning: { bg: '#FFFAEB', color: '#B54708', border: '#FEDF89' },
  error: { bg: '#FEF3F2', color: '#B42318', border: '#FECDCA' },
};

export const Chip = ({ intent = 'primary', children }) => {
  const s = chipStyles[intent] ?? chipStyles.default;
  return (
    <MuiChip
      size="small"
      label={children}
      sx={{
        bgcolor: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        fontWeight: 500,
        fontSize: 12,
        textTransform: 'capitalize',
      }}
    />
  );
};

/* ------------------------------- Divider --------------------------------- */

export const Divider = () => (
  <MuiDivider sx={{ borderColor: colors.gray200 }} />
);

/* ------------------------------- Toggle ---------------------------------- */

export const Toggle = ({ id, checked, onChange, disabled, className }) => (
  <MuiSwitch
    id={id}
    checked={!!checked}
    onChange={onChange}
    disabled={disabled}
    className={className}
    sx={{
      '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        bgcolor: colors.success600,
        opacity: 1,
      },
    }}
  />
);
export const EnableToggle = (props) => (
  <div className="flex items-center gap-2 text-sm">
    <span className={!props.checked ? 'text-gray-900' : 'text-gray-400'}>
      Disable
    </span>
    <Toggle {...props} />
    <span className={props.checked ? 'text-gray-900' : 'text-gray-400'}>
      Enable
    </span>
  </div>
);

/* -------------------------------- Modal ---------------------------------- */

export const Modal = ({
  showModal,
  setShowModal,
  closeOnOutsideClick = true,
  width,
  children,
}) => (
  <MuiDialog
    open={showModal}
    onClose={(_, reason) => {
      if (reason === 'backdropClick' && !closeOnOutsideClick) return;
      setShowModal(false);
    }}
    {...(width ? { maxWidth: false } : { maxWidth: 'md', fullWidth: true })}
    PaperProps={{
      sx: {
        borderRadius: '12px',
        ...(width ? { width, maxWidth: 'calc(100% - 32px)' } : {}),
      },
    }}
  >
    <div className="p-6">{children}</div>
  </MuiDialog>
);

/* ------------------------------ TextInput -------------------------------- */

export const TextInput = ({
  startIcon,
  endIcon,
  error,
  min,
  max,
  step,
  minLength,
  maxLength,
  pattern,
  inputMode,
  inputProps,
  ...rest
}) => (
  <OutlinedInput
    size="small"
    fullWidth
    {...rest}
    inputProps={{
      min,
      max,
      step,
      minLength,
      maxLength,
      pattern,
      inputMode,
      ...inputProps,
    }}
    error={Boolean(error)}
    startAdornment={
      startIcon ? (
        <InputAdornment position="start">{startIcon}</InputAdornment>
      ) : undefined
    }
    endAdornment={
      endIcon ? (
        <InputAdornment position="end">{endIcon}</InputAdornment>
      ) : undefined
    }
    sx={{
      fontSize: 14,
      borderRadius: '8px',
      color: colors.gray700,
      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.gray300 },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.gray400,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.primary600,
        borderWidth: 1,
      },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.error500,
      },
      '&.Mui-disabled, &.MuiInputBase-readOnly': { bgcolor: colors.gray50 },
      '& input': { py: '9px' },
      '&.MuiInputBase-multiline': { py: '9px' },
    }}
  />
);

export const NumberInput = (props) => (
  <TextInput
    type="number"
    {...props}
    onKeyDown={(e) => {
      if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
    }}
    onWheel={(e) => e.target.blur()}
  />
);

/* ----------------------- Label / HelperText / Field ---------------------- */

export const Label = ({ required, className = '', children }) => (
  <label className={`block text-gray-600 text-sm font-medium ${className}`}>
    {children}
    {required && <span className="ml-0.5 text-red-500">*</span>}
  </label>
);

export const HelperText = ({ children, error = true }) => (
  <FormHelperText
    component="div"
    error={error}
    sx={{
      m: 0,
      mt: '4px',
      fontSize: 12,
      color: error ? colors.error600 : colors.gray600,
    }}
  >
    {children}
  </FormHelperText>
);

export const Field = ({
  label,
  required,
  error,
  labelAdornment,
  className = '',
  children,
}) => (
  <div className={`space-y-[6px] ${className}`}>
    {label && (
      <div className="flex items-center gap-1">
        <Label required={required}>{label}</Label>
        {labelAdornment}
      </div>
    )}
    {children}
    {!!error && <HelperText>{error}</HelperText>}
  </div>
);

/* ------------------------------ Skeleton -------------------------------- */
export const Skeleton = ({ width = '100%', height = 16, circle = false }) => (
  <MuiSkeleton
    variant={circle ? 'circular' : 'rounded'}
    width={width}
    height={height}
    sx={{ bgcolor: colors.gray200 }}
  />
);

/* ------------------------------- Tooltip -------------------------------- */
export const Tooltip = ({ content, position = 'top', children }) => (
  <MuiTooltip
    title={content}
    placement={position}
    slotProps={{
      tooltip: {
        sx: {
          bgcolor: colors.gray50,
          color: colors.gray700,
          border: `1px solid ${colors.gray200}`,
          boxShadow: '0 4px 8px rgba(16,24,40,0.10)',
          fontSize: 12,
          fontWeight: 500,
          p: '6px 10px',
        },
      },
    }}
  >
    <span className="inline-flex max-w-full">{children}</span>
  </MuiTooltip>
);

/* ------------------------------- Checkbox ------------------------------- */
export const Checkbox = (props) => (
  <MuiCheckbox
    size="small"
    {...props}
    sx={{
      p: '4px',
      color: colors.gray300,
      '&.Mui-checked': { color: colors.primary600 },
    }}
  />
);

/* ------------------------------ ProgressBar ----------------------------- */
export const ProgressBar = ({ progress, showLabel = true }) => {
  const value = Math.min(100, Math.max(0, progress || 0));
  return (
    <div className="flex w-full items-center gap-2">
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          flex: 1,
          height: 8,
          borderRadius: '4px',
          bgcolor: colors.gray200,
          '& .MuiLinearProgress-bar': {
            bgcolor: colors.primary600,
            borderRadius: '4px',
          },
        }}
      />
      {showLabel && (
        <span className="w-10 text-right text-xs font-medium text-gray-700">
          {Math.round(value)}%
        </span>
      )}
    </div>
  );
};

/* --------------------------------- Tabs --------------------------------- */

export const Tabs = ({
  value,
  onChange,
  items,
  variant = 'underline',
  ariaLabel,
}) => {
  const box = variant === 'box';
  const safeValue = items.some((i) => i.value === value) ? value : false;

  return (
    <MuiTabs
      value={safeValue}
      onChange={(_, v) => onChange(v)}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      aria-label={ariaLabel}
      TabIndicatorProps={{
        sx: box
          ? { display: 'none' }
          : { bgcolor: colors.primary600, height: 2 },
      }}
      sx={{
        minHeight: 0,
        ...(box
          ? { bgcolor: colors.gray200, borderRadius: '8px', p: '4px' }
          : { borderBottom: `1px solid ${colors.gray200}` }),
        '& .MuiTabs-flexContainer': { gap: box ? '4px' : '24px' },
      }}
    >
      {items.map((item) => (
        <MuiTab
          key={item.value}
          value={item.value}
          label={item.label}
          icon={item.icon}
          iconPosition="start"
          disableRipple
          sx={{
            minHeight: box ? 36 : 44,
            minWidth: 0,
            px: box ? '12px' : '4px',
            borderRadius: box ? '6px' : 0,
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 600,
            color: colors.gray500,
            whiteSpace: 'nowrap',
            '&.Mui-selected': box
              ? { color: colors.primary700, bgcolor: colors.gray50 }
              : { color: colors.primary700 },
          }}
        />
      ))}
    </MuiTabs>
  );
};
